import { components } from "@/api-schema";
import { fetchWebPushPublicKey } from "@/common/api/notification/web-push.api";
import { getAppEnvironment } from "@/config/app-environment";
import { isKeyRotated } from "./is-key-rotated";
import { urlBase64ToBuffer } from "./url-base64-to-buffer";

type WebPushSubscribeBody = components["schemas"]["WebPushSubscribeBody"];

interface SyncWebPushSubscriptionOptions {
  permission: NotificationPermission;
  subscribe: (subscription: WebPushSubscribeBody) => Promise<unknown>;
  unsubscribe: (endpoint: string) => Promise<unknown>;
  isCancelled: () => boolean;
}

async function removeWebPushSubscription(
  subscription: PushSubscription | null,
  unsubscribe: SyncWebPushSubscriptionOptions["unsubscribe"],
): Promise<void> {
  if (!subscription) {
    return;
  }

  const { endpoint } = subscription;
  await subscription.unsubscribe().catch(() => undefined);
  await unsubscribe(endpoint).catch(() => undefined);
}

export async function syncWebPushSubscription({
  permission,
  subscribe,
  unsubscribe,
  isCancelled,
}: SyncWebPushSubscriptionOptions): Promise<void> {
  if (isCancelled()) {
    return;
  }

  const registration = await navigator.serviceWorker.register(`/web-push-service-worker.js?env=${getAppEnvironment()}`);
  await navigator.serviceWorker.ready;

  if (isCancelled()) {
    return;
  }

  let subscription = await registration.pushManager.getSubscription();

  if (permission !== "granted") {
    await removeWebPushSubscription(subscription, unsubscribe);
    return;
  }

  const publicKey = await fetchWebPushPublicKey();
  if (!publicKey || isCancelled()) {
    return;
  }

  const applicationServerKey = urlBase64ToBuffer(publicKey);
  if (subscription && isKeyRotated(subscription, applicationServerKey)) {
    await removeWebPushSubscription(subscription, unsubscribe);
    subscription = null;
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }

  if (isCancelled()) {
    return;
  }

  const serializedSubscription = subscription.toJSON();
  if (!serializedSubscription.endpoint || !serializedSubscription.keys?.p256dh || !serializedSubscription.keys.auth) {
    return;
  }

  await subscribe({
    endpoint: serializedSubscription.endpoint,
    keys: {
      p256dh: serializedSubscription.keys.p256dh,
      auth: serializedSubscription.keys.auth,
    },
  });
}
